import React, { useRef, useState } from 'react';
import { Box, Typography, Button, Alert, CircularProgress, TextField } from '@mui/material';
import axios from 'axios';
import { getApiUrl } from '../config';
import { useLanguageContext } from '../contexts/LanguageContext';

declare global {
  interface Window {
    EXIMBAY?: {
      request_pay: (params: Record<string, any>) => void;
    };
  }
}

interface EximbayPaymentWidgetProps {
  /** 통화 단위 기준 금액 (USD: 5.00, KRW: 5000) */
  amount: number;
  currency: 'USD' | 'KRW';
  displayAmount: string;
  onError: (error: string) => void;
  worshipId?: string;
}

interface EximbayReadyResponse {
  success: boolean;
  order_id: string;
  fgkey: string;
  request: Record<string, any>;
  sdk_url: string;
  error?: string;
}

const loadedSdkUrls = new Set<string>();

const loadEximbaySdk = (sdkUrl: string): Promise<void> => {
  if (window.EXIMBAY && loadedSdkUrls.has(sdkUrl)) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${sdkUrl}"]`) as HTMLScriptElement | null;
    if (existing) {
      if (window.EXIMBAY) {
        loadedSdkUrls.add(sdkUrl);
        resolve();
        return;
      }
      existing.addEventListener('load', () => { loadedSdkUrls.add(sdkUrl); resolve(); });
      existing.addEventListener('error', () => reject(new Error('Eximbay SDK load failed')));
      return;
    }

    const script = document.createElement('script');
    script.src = sdkUrl;
    script.async = true;
    script.onload = () => { loadedSdkUrls.add(sdkUrl); resolve(); };
    script.onerror = () => reject(new Error('Eximbay SDK load failed'));
    document.head.appendChild(script);
  });
};

const isMobileDevice = (): boolean =>
  /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '');

/**
 * Eximbay(엑심베이) 결제 위젯
 *
 * 1) 백엔드 /eximbay-payment/ready 호출 -> fgkey + SDK 파라미터 수신
 * 2) Eximbay JS SDK 로드 -> EXIMBAY.request_pay({ fgkey, ...request })
 * 3) 결제창(리다이렉트) -> 완료 후 백엔드 return_url -> /payment-result?provider=eximbay 로 복귀
 */
const EximbayPaymentWidget: React.FC<EximbayPaymentWidgetProps> = ({
  amount,
  currency,
  displayAmount,
  onError,
  worshipId
}) => {
  const { t, currentLanguage } = useLanguageContext();
  const [isPreparing, setIsPreparing] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [buyerName, setBuyerName] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const isRequestingRef = useRef(false);

  const handlePayment = async () => {
    if (isRequestingRef.current) return;
    isRequestingRef.current = true;
    setIsPreparing(true);
    setLocalError(null);

    try {
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await axios.post<EximbayReadyResponse>(
        getApiUrl('eximbay-payment/ready'),
        {
          amount,
          currency,
          language: currentLanguage,
          worship_id: worshipId || null,
          buyer_name: buyerName.trim() || null,
          buyer_email: buyerEmail.trim() || null,
          is_mobile: isMobileDevice()
        },
        { headers }
      );

      const data = response.data;
      if (!data?.success || !data.fgkey || !data.request) {
        throw new Error(data?.error || t('donation.paymentInitError'));
      }

      await loadEximbaySdk(data.sdk_url);
      if (!window.EXIMBAY?.request_pay) {
        throw new Error(t('donation.paymentInitError'));
      }

      // 결제 준비 API 에 보낸 파라미터와 동일한 값을 그대로 전달해야 fgkey 검증을 통과함
      window.EXIMBAY.request_pay({ fgkey: data.fgkey, ...data.request });
    } catch (err: any) {
      console.error('Eximbay 결제 요청 오류:', err);
      const status = err?.response?.status;
      const message =
        status === 503
          ? t('donation.paymentNotConfigured')
          : (err?.response?.data?.error || err?.message || t('donation.paymentInitError'));
      setLocalError(message);
      onError(message);
    } finally {
      isRequestingRef.current = false;
      setIsPreparing(false);
    }
  };

  const fieldSx = {
    mb: 2,
    '& .MuiInputBase-input': { color: 'white' },
    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.8)' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.6)' }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        {t('donation.paymentInfo')}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.85)' }}>
          {t('donation.amountLabel')}: {displayAmount}
        </Typography>
      </Box>

      <TextField
        fullWidth
        size="small"
        label={t('donation.buyerName')}
        value={buyerName}
        onChange={(e) => setBuyerName(e.target.value)}
        inputProps={{ maxLength: 50 }}
        sx={fieldSx}
      />
      <TextField
        fullWidth
        size="small"
        type="email"
        label={t('donation.buyerEmail')}
        value={buyerEmail}
        onChange={(e) => setBuyerEmail(e.target.value)}
        inputProps={{ maxLength: 100 }}
        sx={fieldSx}
      />

      {localError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {localError}
        </Alert>
      )}

      <Button
        variant="contained"
        color="primary"
        fullWidth
        size="large"
        onClick={handlePayment}
        disabled={isPreparing}
        sx={{ py: 1.5, fontSize: '1.1rem', fontWeight: 'bold' }}
      >
        {isPreparing ? (
          <>
            <CircularProgress size={22} sx={{ color: 'white', mr: 1.5 }} />
            {t('donation.preparingPayment')}
          </>
        ) : (
          `${displayAmount} ${t('donation.payButton')}`
        )}
      </Button>

      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.75)' }}>
          {t('donation.eximbayRedirectNotice')}
        </Typography>
      </Box>
    </Box>
  );
};

export default EximbayPaymentWidget;
